import { Suspense, lazy, useState, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import { LoadingScreen } from "./components/LoadingScreen";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { SupporterProvider } from "./context/SupporterContext";
// Eagerly loaded (critical path)
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Tools from "./pages/Tools";

// Lazy loaded components
const MergeTool = lazy(() => import("./pages/tools/MergeTool"));
const RearrangeTool = lazy(() => import("./pages/tools/RearrangeTool"));
const SplitTool = lazy(() => import("./pages/tools/SplitTool"));
const WatermarkTool = lazy(() => import("./pages/tools/WatermarkTool"));
const EncryptTool = lazy(() => import("./pages/tools/EncryptTool"));
const DecryptTool = lazy(() => import("./pages/tools/DecryptTool"));
const PdfToImageTool = lazy(() => import("./pages/tools/PdfToImageTool"));
const PdfToWordTool = lazy(() => import("./pages/tools/PdfToWordTool"));
const PdfToHtmlTool = lazy(() => import("./pages/tools/PdfToHtmlTool"));
const ImageToPdfTool = lazy(() => import("./pages/tools/ImageToPdfTool"));
const WordToPdfTool = lazy(
  () => import("./pages/tools/word-to-pdf/WordToPdfTool"),
);
const MarkdownToPdfTool = lazy(
  () => import("./pages/tools/markdown-to-pdf/MarkdownToPdfTool"),
);
const ExtractFormDataTool = lazy(
  () => import("./pages/tools/ExtractFormDataTool"),
);
const OcrPdfTool = lazy(() => import("./pages/tools/OcrPdfTool"));
const PdfToZipTool = lazy(() => import("./pages/tools/PdfToZipTool"));
const RedactTextTool = lazy(() => import("./pages/tools/RedactTextTool"));
const MailMergeTool = lazy(() => import("./pages/tools/MailMergeTool"));
const BatchStampTool = lazy(() => import("./pages/tools/BatchStampTool"));
const BatesNumberingTool = lazy(
  () => import("./pages/tools/BatesNumberingTool"),
);
const DynamicStampTool = lazy(() => import("./pages/tools/DynamicStampTool"));
const BatchCompressTool = lazy(() => import("./pages/tools/BatchCompressTool"));
const BatchMetadataCleanerTool = lazy(
  () => import("./pages/tools/BatchMetadataCleanerTool"),
);
const BatchSplitTool = lazy(() => import("./pages/tools/BatchSplitTool"));
const BatchTools = lazy(() => import("./pages/BatchTools"));
const BatchLayoutTool = lazy(() => import("./pages/tools/BatchLayoutTool"));
const BatchRotateTool = lazy(() => import("./pages/tools/BatchRotateTool"));
const BatchFlattenTool = lazy(() => import("./pages/tools/BatchFlattenTool"));
const BatchEncryptTool = lazy(() => import("./pages/tools/BatchEncryptTool"));
const BatchDecryptTool = lazy(() => import("./pages/tools/BatchDecryptTool"));
const BatchPageNumbersTool = lazy(
  () => import("./pages/tools/BatchPageNumbersTool"),
);
const BatchExtractTextTool = lazy(
  () => import("./pages/tools/BatchExtractTextTool"),
);
const BatchPdfToImageTool = lazy(
  () => import("./pages/tools/BatchPdfToImageTool"),
);
const BatchRedactTool = lazy(() => import("./pages/tools/BatchRedactTool"));
const BatchLinkManagerTool = lazy(
  () => import("./pages/tools/BatchLinkManagerTool"),
);
const ESignTool = lazy(() => import("./pages/tools/ESignTool"));
const HeaderFooterTool = lazy(() => import("./pages/tools/header-footer"));
const Faq = lazy(() => import("./pages/Faq"));
const CompressTool = lazy(() => import("./pages/tools/CompressTool"));
const CropTool = lazy(() => import("./pages/tools/CropTool"));
const RemovePagesTool = lazy(() => import("./pages/tools/RemovePagesTool"));
const PageNumbersTool = lazy(() => import("./pages/tools/PageNumbersTool"));
const InspectorTool = lazy(() => import("./pages/tools/InspectorTool"));
const EditToolsHub = lazy(() => import("./pages/EditToolsHub"));
const PageSizeTool = lazy(() => import("./pages/tools/edit/PageSizeTool"));
const MarginsTool = lazy(() => import("./pages/tools/edit/MarginsTool"));
const ContrastTool = lazy(() => import("./pages/tools/edit/ContrastTool"));
const EditPdfTool = lazy(() => import("./pages/tools/edit-pdf"));
const LinkManagerTool = lazy(() => import("./pages/tools/LinkManagerTool"));
const BatchOCRTool = lazy(() => import("./pages/tools/BatchOCRTool"));
const EditPdfLayoutTool = lazy(() => import("./pages/tools/edit-pdf-layout"));
const IncreaseSizeTool = lazy(() => import("./pages/tools/IncreaseSizeTool"));
const EnhancePdfTool = lazy(() => import("./pages/tools/EnhancePdfTool"));
const FlattenPdfTool = lazy(() => import("./pages/tools/FlattenPdfTool"));
const AboutPrivacy = lazy(() => import("./pages/AboutPrivacy"));
const MergePdfLanding = lazy(() => import("./pages/MergePdfLanding"));
const CompressPdfLanding = lazy(() => import("./pages/CompressPdfLanding"));
const RemovePagesLanding = lazy(() => import("./pages/RemovePagesLanding"));
const CompressPdfForEmailLanding = lazy(
  () => import("./pages/CompressPdfForEmailLanding"),
);
const MergePdfsForCourtBundlesLanding = lazy(
  () => import("./pages/MergePdfsForCourtBundlesLanding"),
);
const AdjustPdfMarginsForPrintingLanding = lazy(
  () => import("./pages/AdjustPdfMarginsForPrintingLanding"),
);
const StudentsLandingPage = lazy(() => import("./pages/StudentsLandingPage"));
const BusinessLandingPage = lazy(() => import("./pages/BusinessLandingPage"));
const LegalLandingPage = lazy(() => import("./pages/LegalLandingPage"));
const SmallpdfAlternativePage = lazy(
  () => import("./pages/SmallpdfAlternativePage"),
);
const TermsConditions = lazy(() => import("./pages/TermsConditions"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const MergePdfSeoPage = lazy(() => import("./pages/seo/MergePdfSeoPage"));
const CompressPdfSeoPage = lazy(
  () => import("./pages/seo/CompressPdfSeoPage"),
);
const SplitPdfSeoPage = lazy(() => import("./pages/seo/SplitPdfSeoPage"));
const PdfToWordSeoPage = lazy(() => import("./pages/seo/PdfToWordSeoPage"));
const WordToPdfSeoPage = lazy(() => import("./pages/seo/WordToPdfSeoPage"));
const ProgrammaticSeoPage = lazy(() => import("./pages/seo/ProgrammaticSeoPage"));
const ComparisonSeoPage = lazy(() => import("./pages/seo/ComparisonSeoPage"));
const AlternativeSeoPage = lazy(() => import("./pages/seo/AlternativeSeoPage"));
const CategoryHubPage = lazy(() => import("./pages/seo/CategoryHubPage"));
const GlossaryIndexPage = lazy(() => import("./pages/seo/GlossaryIndexPage"));
const GlossaryTermPage = lazy(() => import("./pages/seo/GlossaryTermPage"));

// AI Tools (lazy loaded)
const ChatWithPdfTool = lazy(() => import("./pages/tools/ai/ChatWithPdfTool"));
const QuizGeneratorTool = lazy(
  () => import("./pages/tools/ai/QuizGeneratorTool"),
);
const TimelineGeneratorTool = lazy(
  () => import("./pages/tools/ai/TimelineGeneratorTool"),
);
const SummarizerTool = lazy(() => import("./pages/tools/ai/SummarizerTool"));
const KeyPointsTool = lazy(() => import("./pages/tools/ai/KeyPointsTool"));
const FlashcardTool = lazy(() => import("./pages/tools/ai/FlashcardTool"));
const StudyGuideTool = lazy(() => import("./pages/tools/ai/StudyGuideTool"));
const AIKeyGuide = lazy(() => import("./pages/AIKeyGuide"));

// Analytics Dashboard
const AnalyticsDashboard = lazy(() => import("./pages/Analytics"));

// Document Library
const Library = lazy(() => import("./pages/Library"));

// Template Library
const Templates = lazy(() => import("./pages/Templates"));

// Workflow Automation
const WorkflowsPage = lazy(() => import("./pages/Workflows"));
const WorkflowEditorPage = lazy(() => import("./pages/WorkflowEditor"));

// New tools
const ExtractTextOcrTool = lazy(
  () => import("./pages/tools/ExtractTextOcrTool"),
);
const CreatePdfTool = lazy(() => import("./pages/tools/CreatePdfTool"));
const FormBuilderTool = lazy(() => import("./pages/tools/FormBuilderTool"));
const PdfDiffTool = lazy(() => import("./pages/tools/PdfDiffTool"));
const PdfToExcelTool = lazy(() => import("./pages/tools/PdfToExcelTool"));
const SignFillTool = lazy(() => import("./pages/tools/SignFillTool"));
const PdfTranslatorTool = lazy(() => import("./pages/tools/PdfTranslatorTool"));
const PdfToPptxTool = lazy(() => import("./pages/tools/PdfToPptxTool"));
const ExcelToPdfTool = lazy(() => import("./pages/tools/ExcelToPdfTool"));
const HtmlToPdfTool = lazy(() => import("./pages/tools/HtmlToPdfTool"));
const PdfRepairTool = lazy(() => import("./pages/tools/PdfRepairTool"));
const PageExtractorTool = lazy(() => import("./pages/tools/PageExtractorTool"));
const AddBookmarksTool = lazy(() => import("./pages/tools/AddBookmarksTool"));
const PdfMetadataEditorTool = lazy(
  () => import("./pages/tools/PdfMetadataEditorTool"),
);
const InvoiceGeneratorTool = lazy(
  () => import("./pages/tools/InvoiceGeneratorTool"),
);
const QrCodeGeneratorTool = lazy(
  () => import("./pages/tools/QrCodeGeneratorTool"),
);
const CsvToPdfTool = lazy(() => import("./pages/tools/CsvToPdfTool"));
const PdfToWebpTool = lazy(() => import("./pages/tools/PdfToWebpTool"));
const WebpToPdfTool = lazy(() => import("./pages/tools/WebpToPdfTool"));
const SvgToPdfTool = lazy(() => import("./pages/tools/SvgToPdfTool"));
const EpubToPdfTool = lazy(() => import("./pages/tools/EpubToPdfTool"));
const PdfToEpubTool = lazy(() => import("./pages/tools/PdfToEpubTool"));
const ExtractImagesTool = lazy(() => import("./pages/tools/ExtractImagesTool"));
const SplitPdfBySizeTool = lazy(() => import("./pages/tools/SplitPdfBySizeTool"));
const BatchPdfToPptxTool = lazy(
  () => import("./pages/tools/BatchPdfToPptxTool"),
);
const BatchExcelToPdfTool = lazy(
  () => import("./pages/tools/BatchExcelToPdfTool"),
);
const BatchHtmlToPdfTool = lazy(
  () => import("./pages/tools/BatchHtmlToPdfTool"),
);
const BatchPdfRepairTool = lazy(
  () => import("./pages/tools/BatchPdfRepairTool"),
);
const BatchPageExtractorTool = lazy(
  () => import("./pages/tools/BatchPageExtractorTool"),
);
const BatchAddBookmarksTool = lazy(
  () => import("./pages/tools/BatchAddBookmarksTool"),
);
const BatchMetadataEditorTool = lazy(
  () => import("./pages/tools/BatchMetadataEditorTool"),
);
const PdfPermissionManagerTool = lazy(
  () => import("./pages/tools/PdfPermissionManagerTool"),
);
const PdfSecurityAuditTool = lazy(
  () => import("./pages/tools/PdfSecurityAuditTool"),
);
const PdfSanitizerTool = lazy(() => import("./pages/tools/PdfSanitizerTool"));
const BatchPermissionManagerTool = lazy(
  () => import("./pages/tools/BatchPermissionManagerTool"),
);
const BatchSecurityAuditTool = lazy(
  () => import("./pages/tools/BatchSecurityAuditTool"),
);
const BatchSanitizerTool = lazy(
  () => import("./pages/tools/BatchSanitizerTool"),
);
const BatchExtractImagesTool = lazy(() => import("./pages/tools/BatchExtractImagesTool"));
const BatchSplitPdfBySizeTool = lazy(() => import("./pages/tools/BatchSplitPdfBySizeTool"));
const BatchPdfToWebpTool = lazy(() => import("./pages/tools/BatchPdfToWebpTool"));
const BatchWebpToPdfTool = lazy(() => import("./pages/tools/BatchWebpToPdfTool"));
const BatchSvgToPdfTool = lazy(() => import("./pages/tools/BatchSvgToPdfTool"));
const BatchEpubToPdfTool = lazy(() => import("./pages/tools/BatchEpubToPdfTool"));
const BatchPdfToEpubTool = lazy(() => import("./pages/tools/BatchPdfToEpubTool"));
const BatchMarkdownToPdfTool = lazy(() => import("./pages/tools/BatchMarkdownToPdfTool"));
const BatchCsvToPdfTool = lazy(() => import("./pages/tools/BatchCsvToPdfTool"));
const PdfSignerTool = lazy(() => import("./pages/tools/PdfSignerTool"));
const BatchPdfSignerTool = lazy(() => import("./pages/tools/BatchPdfSignerTool"));
const DynamicWatermarkTool = lazy(() => import("./pages/tools/DynamicWatermarkTool"));
const BatchDynamicWatermarkTool = lazy(() => import("./pages/tools/BatchDynamicWatermarkTool"));
const PdfAConverterTool = lazy(() => import("./pages/tools/PdfAConverterTool"));
const PdfCompareTool = lazy(() => import("./pages/tools/PdfCompareTool"));

// New tools
const BatchCertificateGeneratorTool = lazy(() => import("./pages/tools/BatchCertificateGeneratorTool"));
const DeepMetadataExplorerTool = lazy(() => import("./pages/tools/DeepMetadataExplorerTool"));
const BatchDeepMetadataExplorerTool = lazy(() => import("./pages/tools/BatchDeepMetadataExplorerTool"));
const BatchPdfRuleEngineTool = lazy(() => import("./pages/tools/BatchPdfRuleEngineTool"));

// P2P Tools
const P2PTools = lazy(() => import("./pages/P2PTools"));
const P2PCoAnnotateTool = lazy(() => import("./pages/tools/P2PCoAnnotateTool"));
const P2PReviewApprovalTool = lazy(() => import("./pages/tools/P2PReviewApprovalTool"));
const P2PFormFillSignTool = lazy(() => import("./pages/tools/P2PFormFillSignTool"));
const P2PRealtimeCollabTool = lazy(() => import("./pages/tools/P2PRealtimeCollabTool"));
const P2PPresentationTool = lazy(() => import("./pages/tools/P2PPresentationTool"));
const P2PRedlineTool = lazy(() => import("./pages/tools/P2PRedlineTool"));
const P2PWhiteboardTool = lazy(() => import("./pages/tools/P2PWhiteboardTool"));
const P2PMultiSignerTool = lazy(() => import("./pages/tools/P2PMultiSignerTool"));
const P2PClassroomTool = lazy(() => import("./pages/tools/P2PClassroomTool"));
const P2PContractNegotiationTool = lazy(() => import("./pages/tools/P2PContractNegotiationTool"));
const P2PDocComparisonTool = lazy(() => import("./pages/tools/P2PDocComparisonTool"));

import { Header } from "./components/layout/Header";
import { Footer } from "./components/layout/Footer";
import { AppSidebar } from "./components/AppSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { ToolHistoryProvider } from "@/context/ToolHistoryContext";
import { AISettingsProvider } from "@/context/AISettingsContext";
import { ScrollToTop } from "./components/ScrollToTop";
import { ToolSeoContentSection } from "./components/seo/ToolSeoContentSection";

// Loading fallback for lazy components
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-foreground/20 border-t-foreground" />
  </div>
);

const AppLayout = () => {
  const location = useLocation();
  const path = location.pathname;
  const showSidebar =
    path.startsWith("/tools") || path.startsWith("/batch-tools") || path.startsWith("/p2p-tools");

  return (
    <AISettingsProvider>
      <SupporterProvider>
        <SidebarProvider defaultOpen={false}>
          <ToolHistoryProvider>
            <div className="flex min-h-screen w-full bg-background text-foreground font-sans">
              {showSidebar && <AppSidebar />}
              <div className="flex min-h-screen flex-1 flex-col">
                <Header />
                <main className="flex-1">
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route path="/" element={<Index />} />
                      <Route path="/students" element={<StudentsLandingPage />} />
                      <Route path="/for-students" element={<Navigate to="/students" replace />} />
                      <Route path="/business" element={<BusinessLandingPage />} />
                      <Route path="/for-business" element={<Navigate to="/business" replace />} />
                      <Route path="/for-small-business" element={<Navigate to="/business" replace />} />
                      <Route path="/legal" element={<LegalLandingPage />} />
                      <Route path="/for-legal" element={<Navigate to="/legal" replace />} />
                      <Route
                        path="/smallpdf-alternative"
                        element={<SmallpdfAlternativePage />}
                      />
                      <Route path="/how-to/:slug" element={<ProgrammaticSeoPage />} />
                      <Route path="/guides/:slug" element={<ProgrammaticSeoPage />} />
                      <Route path="/compare/:slug" element={<ComparisonSeoPage />} />
                      <Route path="/alternatives/:slug" element={<AlternativeSeoPage />} />
                      <Route path="/tools/category/:categoryId" element={<CategoryHubPage />} />
                      <Route path="/glossary" element={<GlossaryIndexPage />} />
                      <Route path="/glossary/:termId" element={<GlossaryTermPage />} />
                      <Route path="/merge-pdf" element={<MergePdfSeoPage />} />
                      <Route
                        path="/compress-pdf"
                        element={<CompressPdfSeoPage />}
                      />
                      <Route path="/split-pdf" element={<SplitPdfSeoPage />} />
                      <Route
                        path="/pdf-to-word"
                        element={<PdfToWordSeoPage />}
                      />
                      <Route
                        path="/word-to-pdf"
                        element={<WordToPdfSeoPage />}
                      />
                      <Route path="/tools" element={<Tools />} />
                      <Route path="/batch-tools" element={<BatchTools />} />
                      <Route
                        path="/merge-pdf-locally"
                        element={<MergePdfLanding />}
                      />
                      <Route path="/compress" element={<CompressPdfLanding />} />
                      <Route
                        path="/remove-pdf-pages-offline"
                        element={<RemovePagesLanding />}
                      />
                      <Route
                        path="/compress-pdf-for-email"
                        element={<CompressPdfForEmailLanding />}
                      />
                      <Route
                        path="/merge-pdfs-for-court-bundles"
                        element={<MergePdfsForCourtBundlesLanding />}
                      />
                      <Route
                        path="/adjust-pdf-margins-for-printing"
                        element={<AdjustPdfMarginsForPrintingLanding />}
                      />
                      <Route
                        path="/tools/edit-layout"
                        element={<EditToolsHub />}
                      />
                      <Route
                        path="/tools/edit-pdf"
                        element={<EditPdfTool cleanUI={true} />}
                      />
                      <Route
                        path="/tools/link-manager"
                        element={<LinkManagerTool />}
                      />
                      <Route
                        path="/tools/edit/page-size"
                        element={<PageSizeTool />}
                      />
                      <Route
                        path="/tools/edit/margins"
                        element={<MarginsTool />}
                      />
                      <Route
                        path="/tools/edit/contrast"
                        element={<ContrastTool />}
                      />
                      <Route path="/tools/merge" element={<MergeTool />} />
                      <Route
                        path="/tools/rearrange"
                        element={<RearrangeTool />}
                      />
                      <Route
                        path="/tools/convert/image-to-pdf"
                        element={<ImageToPdfTool />}
                      />
                      <Route
                        path="/tools/word-to-pdf"
                        element={<WordToPdfTool />}
                      />
                      <Route
                        path="/tools/markdown-to-pdf"
                        element={<MarkdownToPdfTool />}
                      />
                      <Route
                        path="/tools/extract-form-data"
                        element={<ExtractFormDataTool />}
                      />
                      <Route path="/tools/ocr-pdf" element={<OcrPdfTool />} />
                      <Route
                        path="/tools/pdf-to-zip"
                        element={<PdfToZipTool />}
                      />
                      <Route
                        path="/tools/redact-text"
                        element={<RedactTextTool />}
                      />
                      <Route
                        path="/tools/mail-merge"
                        element={<MailMergeTool />}
                      />
                      <Route
                        path="/batch-tools/stamp"
                        element={<BatchStampTool />}
                      />
                      <Route
                        path="/batch-tools/bates-numbering"
                        element={<BatesNumberingTool />}
                      />
                      <Route
                        path="/batch-tools/dynamic-stamp"
                        element={<DynamicStampTool />}
                      />
                      <Route
                        path="/batch-tools/compress"
                        element={<BatchCompressTool />}
                      />
                      <Route
                        path="/batch-tools/metadata"
                        element={<BatchMetadataCleanerTool />}
                      />
                      <Route
                        path="/batch-tools/split"
                        element={<BatchSplitTool />}
                      />
                      <Route
                        path="/batch-tools/layout"
                        element={<BatchLayoutTool />}
                      />
                      <Route
                        path="/batch-tools/rotate"
                        element={<BatchRotateTool />}
                      />
                      <Route
                        path="/batch-tools/flatten"
                        element={<BatchFlattenTool />}
                      />
                      <Route
                        path="/batch-tools/encrypt"
                        element={<BatchEncryptTool />}
                      />
                      <Route
                        path="/batch-tools/decrypt"
                        element={<BatchDecryptTool />}
                      />
                      <Route
                        path="/batch-tools/page-numbers"
                        element={<BatchPageNumbersTool />}
                      />
                      <Route
                        path="/batch-tools/extract-text"
                        element={<BatchExtractTextTool />}
                      />
                      <Route path="/batch-tools/extract-images" element={<BatchExtractImagesTool />} />
                      <Route path="/batch-tools/split-by-size" element={<BatchSplitPdfBySizeTool />} />
                      <Route path="/batch-tools/pdf-to-webp" element={<BatchPdfToWebpTool />} />
                      <Route path="/batch-tools/webp-to-pdf" element={<BatchWebpToPdfTool />} />
                      <Route path="/batch-tools/svg-to-pdf" element={<BatchSvgToPdfTool />} />
                      <Route path="/batch-tools/epub-to-pdf" element={<BatchEpubToPdfTool />} />
                      <Route path="/batch-tools/pdf-to-epub" element={<BatchPdfToEpubTool />} />
                      <Route path="/batch-tools/markdown-to-pdf" element={<BatchMarkdownToPdfTool />} />
                      <Route path="/batch-tools/excel-to-pdf" element={<BatchCsvToPdfTool />} />
                      <Route
                        path="/batch-tools/pdf-to-image"
                        element={<BatchPdfToImageTool />}
                      />
                      <Route
                        path="/batch-tools/redact"
                        element={<BatchRedactTool />}
                      />
                      <Route
                        path="/batch-tools/link-manager"
                        element={<BatchLinkManagerTool />}
                      />
                      <Route path="/batch-tools/ocr" element={<BatchOCRTool />} />
                      <Route
                        path="/batch-tools/pdf-to-pptx"
                        element={<BatchPdfToPptxTool />}
                      />
                      <Route
                        path="/batch-tools/excel-to-pdf"
                        element={<BatchExcelToPdfTool />}
                      />
                      <Route
                        path="/batch-tools/html-to-pdf"
                        element={<BatchHtmlToPdfTool />}
                      />
                      <Route
                        path="/batch-tools/pdf-repair"
                        element={<BatchPdfRepairTool />}
                      />
                      <Route
                        path="/batch-tools/page-extractor"
                        element={<BatchPageExtractorTool />}
                      />
                      <Route
                        path="/batch-tools/add-bookmarks"
                        element={<BatchAddBookmarksTool />}
                      />
                      <Route
                        path="/batch-tools/metadata-editor"
                        element={<BatchMetadataEditorTool />}
                      />
                      <Route
                        path="/batch-tools/permission-manager"
                        element={<BatchPermissionManagerTool />}
                      />
                      <Route
                        path="/batch-tools/security-audit"
                        element={<BatchSecurityAuditTool />}
                      />
                      <Route
                        path="/batch-tools/sanitizer"
                        element={<BatchSanitizerTool />}
                      />
                      <Route path="/tools/esign-pdf" element={<ESignTool />} />
                      <Route
                        path="/tools/header-footer"
                        element={<HeaderFooterTool />}
                      />
                      <Route path="/tools/split" element={<SplitTool />} />
                      <Route
                        path="/tools/watermark"
                        element={<WatermarkTool />}
                      />
                      <Route path="/tools/encrypt" element={<EncryptTool />} />
                      <Route path="/tools/pdf-signer" element={<PdfSignerTool />} />
                      <Route path="/batch-tools/pdf-signer" element={<BatchPdfSignerTool />} />
                      <Route path="/tools/dynamic-watermark" element={<DynamicWatermarkTool />} />
                      <Route path="/batch-tools/dynamic-watermark" element={<BatchDynamicWatermarkTool />} />
                      <Route path="/tools/pdf-a-converter" element={<PdfAConverterTool />} />
                      <Route path="/tools/pdf-compare" element={<PdfCompareTool />} />
                      {/* New tools */}
                      <Route path="/batch-tools/certificate-generator" element={<BatchCertificateGeneratorTool />} />
                      <Route path="/tools/deep-metadata-explorer" element={<DeepMetadataExplorerTool />} />
                      <Route path="/batch-tools/deep-metadata-explorer" element={<BatchDeepMetadataExplorerTool />} />
                      <Route path="/batch-tools/pdf-rule-engine" element={<BatchPdfRuleEngineTool />} />
                      {/* P2P Tools */}
                      <Route path="/p2p-tools" element={<P2PTools />} />
                      <Route path="/p2p-tools/co-annotate" element={<P2PCoAnnotateTool />} />
                      <Route path="/p2p-tools/review-approve" element={<P2PReviewApprovalTool />} />
                      <Route path="/p2p-tools/form-fill-sign" element={<P2PFormFillSignTool />} />
                      <Route path="/p2p-tools/realtime-collab" element={<P2PRealtimeCollabTool />} />
                      <Route path="/p2p-tools/presentation" element={<P2PPresentationTool />} />
                      <Route path="/p2p-tools/redline" element={<P2PRedlineTool />} />
                      <Route path="/p2p-tools/whiteboard" element={<P2PWhiteboardTool />} />
                      <Route path="/p2p-tools/multi-signer" element={<P2PMultiSignerTool />} />
                      <Route path="/p2p-tools/classroom" element={<P2PClassroomTool />} />
                      <Route path="/p2p-tools/contract-negotiation" element={<P2PContractNegotiationTool />} />
                      <Route path="/p2p-tools/doc-comparison" element={<P2PDocComparisonTool />} />
                      <Route path="/tools/decrypt" element={<DecryptTool />} />
                      <Route path="/tools/compress" element={<CompressTool />} />
                      <Route path="/tools/crop" element={<CropTool />} />
                      <Route
                        path="/tools/remove-pages"
                        element={<RemovePagesTool />}
                      />
                      <Route
                        path="/tools/page-numbers"
                        element={<PageNumbersTool />}
                      />
                      <Route
                        path="/tools/inspector"
                        element={<InspectorTool />}
                      />
                      <Route
                        path="/tools/convert/pdf-to-word"
                        element={<PdfToWordTool />}
                      />
                      <Route
                        path="/tools/convert/pdf-to-image"
                        element={<PdfToImageTool />}
                      />
                      <Route
                        path="/tools/convert/pdf-to-html"
                        element={<PdfToHtmlTool />}
                      />
                      {/* Layout Tools */}
                      <Route
                        path="/tools/edit-pdf-layout"
                        element={<EditPdfLayoutTool />}
                      />
                      <Route
                        path="/tools/increase-pdf-size"
                        element={<IncreaseSizeTool />}
                      />
                      <Route
                        path="/tools/enhance-pdf"
                        element={<EnhancePdfTool />}
                      />
                      <Route
                        path="/tools/extract-text-ocr"
                        element={<ExtractTextOcrTool />}
                      />
                      <Route
                        path="/tools/create-pdf"
                        element={<CreatePdfTool />}
                      />
                      <Route
                        path="/tools/invoice-generator"
                        element={<InvoiceGeneratorTool />}
                      />
                      <Route
                        path="/tools/qrcode-generator"
                        element={<QrCodeGeneratorTool />}
                      />
                      <Route
                        path="/tools/csv-to-pdf"
                        element={<CsvToPdfTool />}
                      />
                      <Route
                        path="/tools/extract-images"
                        element={<ExtractImagesTool />}
                      />
                      <Route
                        path="/tools/split-by-size"
                        element={<SplitPdfBySizeTool />}
                      />
                      <Route path="/tools/pdf-to-webp" element={<PdfToWebpTool />} />
                      <Route path="/tools/webp-to-pdf" element={<WebpToPdfTool />} />
                      <Route path="/tools/svg-to-pdf" element={<SvgToPdfTool />} />
                      <Route path="/tools/epub-to-pdf" element={<EpubToPdfTool />} />
                      <Route path="/tools/pdf-to-epub" element={<PdfToEpubTool />} />
                      <Route
                        path="/tools/form-builder"
                        element={<FormBuilderTool />}
                      />
                      <Route path="/tools/pdf-diff" element={<PdfDiffTool />} />
                      <Route
                        path="/tools/pdf-to-excel"
                        element={<PdfToExcelTool />}
                      />
                      <Route path="/tools/sign-fill" element={<SignFillTool />} />
                      <Route
                        path="/tools/pdf-translator"
                        element={<PdfTranslatorTool />}
                      />
                      <Route
                        path="/tools/pdf-to-pptx"
                        element={<PdfToPptxTool />}
                      />
                      <Route
                        path="/tools/excel-to-pdf"
                        element={<ExcelToPdfTool />}
                      />
                      <Route
                        path="/tools/html-to-pdf"
                        element={<HtmlToPdfTool />}
                      />
                      <Route
                        path="/tools/pdf-repair"
                        element={<PdfRepairTool />}
                      />
                      <Route
                        path="/tools/page-extractor"
                        element={<PageExtractorTool />}
                      />
                      <Route
                        path="/tools/add-bookmarks"
                        element={<AddBookmarksTool />}
                      />
                      <Route
                        path="/tools/pdf-permissions"
                        element={<PdfPermissionManagerTool />}
                      />
                      <Route
                        path="/tools/pdf-security-audit"
                        element={<PdfSecurityAuditTool />}
                      />
                      <Route
                        path="/tools/pdf-sanitizer"
                        element={<PdfSanitizerTool />}
                      />
                      <Route
                        path="/tools/pdf-metadata"
                        element={<PdfMetadataEditorTool />}
                      />
                      <Route
                        path="/tools/flatten-pdf"
                        element={<FlattenPdfTool />}
                      />
                      {/* AI Tools */}
                      <Route
                        path="/tools/ai/chat"
                        element={<ChatWithPdfTool />}
                      />
                      <Route
                        path="/tools/ai/quiz"
                        element={<QuizGeneratorTool />}
                      />
                      <Route
                        path="/tools/ai/timeline"
                        element={<TimelineGeneratorTool />}
                      />
                      <Route
                        path="/tools/ai/summarize"
                        element={<SummarizerTool />}
                      />
                      <Route
                        path="/tools/ai/keypoints"
                        element={<KeyPointsTool />}
                      />
                      <Route
                        path="/tools/ai/flashcards"
                        element={<FlashcardTool />}
                      />
                      <Route
                        path="/tools/ai/studyguide"
                        element={<StudyGuideTool />}
                      />
                      <Route path="/ai-key-guide" element={<AIKeyGuide />} />
                      <Route path="/analytics" element={<AnalyticsDashboard />} />
                      <Route path="/library" element={<Library />} />
                      <Route path="/templates" element={<Templates />} />
                      <Route path="/workflows" element={<WorkflowsPage />} />
                      <Route path="/workflows/:id" element={<WorkflowEditorPage />} />
                      <Route path="/faq" element={<Faq />} />
                      <Route path="/blog" element={<Blog />} />
                      <Route path="/blog/:slug" element={<BlogPost />} />
                      <Route
                        path="/blog/pdf edit"
                        element={<Navigate to="/blog/pdf-edit" replace />}
                      />
                      <Route
                        path="/blog/client-side pdf tools"
                        element={
                          <Navigate to="/blog/client-side-pdf-tools" replace />
                        }
                      />
                      <Route
                        path="/blod/client-side-pdf-tools"
                        element={
                          <Navigate to="/blog/client-side-pdf-tools" replace />
                        }
                      />
                      <Route
                        path="/blod/client-side pdf tools"
                        element={
                          <Navigate to="/blog/client-side-pdf-tools" replace />
                        }
                      />
                      <Route path="/about" element={<Navigate to="/about-privacy" replace />} />
                      <Route path="/about-privacy" element={<AboutPrivacy />} />
                      <Route path="/terms" element={<TermsConditions />} />
                      <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
                      <Route path="/privacy" element={<PrivacyPolicy />} />

                      {/* Tool route aliases for deep-link resilience */}
                      <Route path="/edit-pdf" element={<Navigate to="/tools/edit-pdf" replace />} />
                      <Route path="/edit-pdf-layout" element={<Navigate to="/tools/edit-pdf-layout" replace />} />
                      <Route path="/batch-stamp-pdf" element={<Navigate to="/batch-tools/stamp" replace />} />
                      <Route path="/rearrange-pdf" element={<Navigate to="/tools/rearrange" replace />} />
                      <Route path="/remove-pages-pdf" element={<Navigate to="/tools/remove-pages" replace />} />
                      <Route path="/extract-pages-pdf" element={<Navigate to="/tools/page-extractor" replace />} />
                      <Route path="/pdf-to-excel" element={<Navigate to="/tools/pdf-to-excel" replace />} />
                      <Route path="/excel-to-pdf" element={<Navigate to="/tools/excel-to-pdf" replace />} />
                      <Route path="/pdf-to-powerpoint" element={<Navigate to="/tools/pdf-to-pptx" replace />} />
                      <Route path="/image-to-pdf" element={<Navigate to="/tools/convert/image-to-pdf" replace />} />
                      <Route path="/html-to-pdf" element={<Navigate to="/tools/html-to-pdf" replace />} />
                      <Route path="/ocr-pdf" element={<Navigate to="/tools/ocr-pdf" replace />} />
                      <Route path="/pdf-security-audit" element={<Navigate to="/tools/pdf-security-audit" replace />} />
                      <Route path="/batch-pdf-security-audit" element={<Navigate to="/batch-tools/security-audit" replace />} />
                      <Route path="/pdf-sanitizer" element={<Navigate to="/tools/pdf-sanitizer" replace />} />
                      <Route path="/encrypt-pdf" element={<Navigate to="/tools/encrypt" replace />} />
                      <Route path="/batch-compress-pdf" element={<Navigate to="/batch-tools/compress" replace />} />
                      <Route path="/esign-pdf" element={<Navigate to="/tools/esign-pdf" replace />} />
                      <Route path="/pdf-signer" element={<Navigate to="/tools/pdf-signer" replace />} />
                      <Route path="/chat-with-pdf" element={<Navigate to="/tools/ai/chat" replace />} />
                      <Route path="/summarize-pdf" element={<Navigate to="/tools/ai/summarize" replace />} />
                      <Route path="/pdf-key-points" element={<Navigate to="/tools/ai/keypoints" replace />} />
                      <Route path="/quiz-generator-pdf" element={<Navigate to="/tools/ai/quiz" replace />} />
                      <Route path="/flashcard-generator-pdf" element={<Navigate to="/tools/ai/flashcards" replace />} />
                      <Route path="/timeline-generator-pdf" element={<Navigate to="/tools/ai/timeline" replace />} />

                      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                  <ToolSeoContentSection />
                </main>
                <Footer />
              </div>
            </div>
          </ToolHistoryProvider>
        </SidebarProvider>
      </SupporterProvider>
    </AISettingsProvider>
  );
};

import RouteTracker from "./components/analytics/RouteTracker";
import { PwaPrompt } from "./components/PwaPrompt";

const App = () => {
  const isPrerendering =
    typeof navigator !== "undefined" &&
    /ReactSnap/i.test(navigator.userAgent || "");

  const [showLoading, setShowLoading] = useState(() => {
    if (isPrerendering) {
      return false;
    }

    // Only show loading screen once per session
    if (typeof window !== "undefined") {
      return !sessionStorage.getItem("pdfworkspace-loaded");
    }
    return false;
  });

  const handleLoadingComplete = () => {
    sessionStorage.setItem("pdfworkspace-loaded", "true");
    setShowLoading(false);
  };

  return (
    <>
      {showLoading && <LoadingScreen onComplete={handleLoadingComplete} />}
      {!isPrerendering && <Analytics />}
      {!isPrerendering && <SpeedInsights />}
      <Toaster />
      <Sonner />
      <PwaPrompt />
      <BrowserRouter>
        <RouteTracker />
        <ScrollToTop />
        <AppLayout />
      </BrowserRouter>
    </>
  );
};

export default App;
